import { ReactNode, useMemo } from 'react';
import { FormControl, FormHelperText, FormLabel, Stack, SxProps } from '@mui/material';
import { useFormContext } from 'react-hook-form';

type ConnectedFormFieldGroupProps = {
  label: string;
  formNames: string[];
  children: ReactNode;
  readonly?: boolean;
  required?: boolean;
  helperText?: string;
  sx?: SxProps;
};

export const ConnectedFormFieldGroup = ({
  label,
  required,
  readonly,
  helperText,
  children,
  formNames,
  sx,
}: ConnectedFormFieldGroupProps) => {
  const formContext = useFormContext();

  const hasFieldError = useMemo(() => {
    return formNames.some((name) => !!formContext?.getFieldState(name, formContext?.formState).error);
  }, [formContext, formNames]);

  if (!formContext && !readonly) {
    throw new Error('Form context is not available. Use ConnectedFormFieldGroup only in FormProvider.');
  }

  if (readonly) {
    return children;
  }

  return (
    <FormControl
      fullWidth
      variant={'standard'}
      required={required}
      component={'fieldset'}
      error={hasFieldError}
      sx={{
        px: 2,
        py: 1,
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: hasFieldError ? 'error.main' : 'grey.300',
        borderRadius: 1,
        ...sx,
      }}
    >
      <FormLabel component={'legend'}>{label}</FormLabel>
      <Stack mb={2}>{children}</Stack>
      <FormHelperText>{helperText}</FormHelperText>
    </FormControl>
  );
};
